// build v2.0.1
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, MemoryRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { GlobalSecurityGate } from "@/components/auth/GlobalSecurityGate";

import Index from "./pages/Index";
import Support from "./pages/Support";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import RefundPolicy from "./pages/RefundPolicy";
import Impressum from "./pages/Impressum";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Pricing from "./pages/Pricing";
import Plans from "./pages/Plans";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import { AuthStart } from "./pages/AuthStart";
import { AuthStartMicrosoft } from "./pages/AuthStartMicrosoft";
import { AuthCallbackMicrosoft } from "./pages/AuthCallbackMicrosoft";

import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancelled from "./pages/PaymentCancelled";
import NotFound from "./pages/NotFound";
import AddContactFromEmailOutlook from "./pages/blog/AddContactFromEmailOutlook";
import { AddInWrapper } from "@/components/addin/AddInWrapper";

const queryClient = new QueryClient();

const supportsBrowserHistory = () => {
  return typeof window.history?.pushState === 'function' &&
    typeof window.history?.replaceState === 'function';
};

const App = () => {
  const pathname = window.location.pathname;
  const initialEntry = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  
  // Detect add-in mode from query parameter (Outlook adds ?et=) or pathname
  const isAddinMode = window.location.search.includes('et=') || 
                      pathname.includes('/addin');
  
  const isDialogMode = window.location.search.includes('mode=dialog');
  
  // Handle Add-in mode separately with GlobalSecurityGate protection
  if (isAddinMode) {
    return (
      <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <MemoryRouter initialEntries={['/addin']}>
              <GlobalSecurityGate>
                <AuthProvider>
                  <SubscriptionProvider>
                    <ScrollToTop />
                    <Routes>
                      <Route path="/addin-debug" element={
                        <div className="min-h-screen flex items-center justify-center bg-primary/10">
                          <div className="text-center space-y-4">
                            <h1 className="text-2xl font-bold">ADDIN DEBUG ROUTE</h1>
                            <p className="text-muted-foreground">If you see this, routing works!</p>
                          </div>
                        </div>
                      } />
                      <Route path="/addin" element={<AddInWrapper />} />
                    </Routes>
                  </SubscriptionProvider>
                </AuthProvider>
              </GlobalSecurityGate>
            </MemoryRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </ThemeProvider>
      </HelmetProvider>
    );
  }
  
  const Router = supportsBrowserHistory() ? BrowserRouter : MemoryRouter;
  const routerProps = supportsBrowserHistory() ? {} : { initialEntries: [initialEntry] };

  // Web app uses BrowserRouter when the History API is available. Some Outlook/Safari
  // auth windows expose a broken History API, so MemoryRouter prevents a blank page.
  return (
    <HelmetProvider>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <Router {...routerProps}>
            <GlobalSecurityGate>
              <AuthProvider>
                <SubscriptionProvider>
                  <ScrollToTop />
                  <Routes>
                {/* OAuth Routes - Protected by GlobalSecurityGate */}
                <Route path="/auth-start" element={<AuthStart />} />
                <Route path="/auth-start-microsoft" element={<AuthStartMicrosoft />} />
                <Route path="/auth-callback" element={<AuthCallback />} />
                <Route path="/auth-callback-microsoft" element={<AuthCallbackMicrosoft />} />
                
                
                <Route path="/" element={<Index />} />
                <Route path="/blog/add-contact-from-email-outlook" element={<AddContactFromEmailOutlook />} />
                <Route path="/support" element={<Support />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route 
                  path="/plans" 
                  element={
                    <ProtectedRoute>
                      <Plans />
                    </ProtectedRoute>
                  } 
                />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route 
                  path="/addin-debug" 
                  element={
                    <ProtectedRoute>
                      <div className="min-h-screen flex items-center justify-center bg-primary/10">
                        <div className="text-center space-y-4">
                          <h1 className="text-2xl font-bold">ADDIN DEBUG ROUTE</h1>
                          <p className="text-muted-foreground">If you see this, routing works!</p>
                        </div>
                      </div>
                    </ProtectedRoute>
                  } 
                />
                <Route
                  path="/addin"
                  element={
                    <ProtectedRoute>
                      <AddInWrapper />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings" 
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute>
                      <Admin />
                    </ProtectedRoute>
                  } 
                />
                <Route path="*" element={<NotFound />} />
                </Routes>
              </SubscriptionProvider>
            </AuthProvider>
            </GlobalSecurityGate>
          </Router>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;
